<template>
	<!-- overflow-hidden clips the image scaled beyond bounds by the ken burns animation -->
	<component
		:is="tag"
		:id="blok._uid"
		:to="bannerLink"
		:title="blok.bg_image.alt"
		class="relative block"
		:class="{'overflow-hidden': blok.enable_background_animation}"
		@click="addPromotionClick(promo)"
	>
		<div
			v-if="blok.button.cached_url || blok.button.url"
			class="absolute inset-x-0 bottom-4 z-20 flex justify-center sm:bottom-16"
		>
			<v-button
				:to="
					blok.button.cached_url && !bannerLink
						? formatSbUrl(blok.button)
						: undefined
				"
				:aria-label="blok.button_text"
				theme="gray"
			>
				{{ blok.button_text }}
			</v-button>
		</div>

		<nuxt-picture
			v-if="blok.bg_image?.filename"
			:src="blok.bg_image.filename"
			:alt="altText"
			class="block"
			:loading="index === 0 || (componentIndex ?? 1) === 0 ? 'eager' : 'lazy'"
			:preload="
				index === 0 || (componentIndex ?? 1) === 0
					? {fetchPriority: 'high'}
					: false
			"
			v-bind="sbImageDimensions(blok.bg_image.filename)"
			provider="storyblok"
			sizes="xs:100vw sm:640px md:768px lg:1024px xl:1280px 2xl:1536px"
			:img-attrs="{
				// eslint-disable-next-line max-len
				class:
					'w-full object-cover' +
					(blok.enable_background_animation
						? ' motion-safe:animate-ken-burns'
						: '') +
					(locale === 'et' ? ' xl:aspect-[2.35/1]' : ''),
				fetchpriority: (componentIndex ?? 5) <= 4 ? 'high' : 'low',
			}"
		/>
	</component>
</template>

<script setup lang="ts">
import type {BlokComponent} from "@pkg/shared/types";
import {NuxtLink} from "#components";

import {usePromotion} from "@pkg/shared/composables/use-promotion";
import formatSbUrl from "@pkg/shared/utils/format-sb-url";
import sbImageDimensions from "@pkg/shared/utils/sb-image-dimensions";

const properties = defineProps<{
	blok: Extract<
		BlokComponent,
		{
			component:
				| "sb-cta"
				| "sb-cta-big"
				| "sb-cta-button"
				| "sb-cta-content"
				| "sb-cta-image"
				| "sb-cta-narrow"
				| "sb-cta-countdown";
		}
	>;
	index?: number;
	componentIndex?: number;
}>();

const route = useRoute();
const {addPromotionClick} = usePromotion();
const {locale} = useI18n();

const bannerLink = computed(() =>
	formatSbUrl(properties.blok.whole_element_cta_link),
);

const tag = computed(() => {
	if (bannerLink.value) {
		return NuxtLink;
	}
	return "div";
});

function convertString(string: string) {
	const words = string.split("-");
	const convertedString = words
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
	return convertedString;
}

const promo = computed(() => ({
	creative_name: convertString(properties.blok.component),
	creative_slot: `${properties.blok.component} / ${properties.index}`, // component name / index

	promotion_id: properties.blok._uid,
	promotion_name: properties.blok.name,
	source_url: route.fullPath,
	target_url: bannerLink.value,
}));

const altText = computed(
	() => properties.blok.bg_image.alt ?? properties.blok.title,
);
</script>
